/**
 * Cumulative Premium V2 Widget
 *
 * Live data over the QF Centrifugo stream (premium profile + 1m OHLC); REST for initial history.
 *
 * View: Premium only — net call (bought+sold), net put (bought+sold), and
 * pressure (pre-computed: net_call - net_put).
 *
 * ⚠️ The Calls and Puts views were removed from the toolbar; the widget is
 * Net Sweeper FLOW and Premium is the view that name describes. Their chart
 * branches and the `showNet` state they used are still in the file, unreachable,
 * in case the switcher returns — see the note on `viewMode`.
 *
 * Features:
 * - Type-and-Enter ticker (no autocomplete)
 * - OHLC candlestick chart for spot price
 * - Exp filter: Next expiration only, or all
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePlotly } from '../hooks/usePlotly';
import { useMarketOpen } from '../hooks/useMarketOpen';
import {
  bucketToDate,
  mergePremiumHistory,
  premiumMinuteKey,
  premiumV2Service,
  shouldAcceptLivePremium,
  subscribePremiumLive,
  type PremiumTimeseriesPoint,
  type PremiumSnapshot,
  type OHLCPoint,
} from '../services/premiumV2Service';
import { onQfConnected, qfSubscribe } from '../services/qfClient';
import { TickerLinkButton } from './TickerLinkButton';
import { useTickerLink } from '../contexts/TickerLinkContext';
import {
  isWeekend,
  isBeforeMarketOpen,
  getEffectiveMarketDate,
  getTodayDateET,
  formatDateForDisplay,
} from '../utils/marketDate';
import { profileService } from '../services/profileService';
import { getOptionsDisplayTicker, normalizeOptionsTicker, underlyingPriceTicker } from '../utils/tickerDisplay';
import { createCoalescedScheduler } from '../utils/scheduleUpdate';

/** RTH options session is ~390 minutes; keep a small cushion. */
const MAX_PREMIUM_POINTS = 500;
/** Dense /v1/ohlc can include 04:00–20:00 ET (16h). Do not share the spine cap. */
const MAX_OHLC_POINTS = 960;

interface Props {
  windowId: string;
  onRemove: () => void;
}

// Colors matching the dark theme
const COLORS = {
  // Panel, not page. `--color-dark-surface` is the tool-window body;
  // `--color-dark-bg` (#0A0A0A) is what sits BEHIND the windows, so using it
  // here made the plot area read as a hole in the widget.
  background: '#161616',
  cardBg: '#161616',
  // Neutral white at 5%, so gridlines take the tint of whatever they sit on.
  // Was #1e2433, a blue-tinted grey that is not in the palette and read as a
  // faint blue cast across the plot.
  gridLines: 'rgba(255,255,255,0.05)',
  text: '#C7C2B8', // --color-text-muted. Was #9ca3af, stock Tailwind grey.
  callBought: '#1C76A2', // bull-blue
  callSold: '#0F3D54', // darker bull-blue
  callNet: '#3E8BB0', // lighter bull-blue
  putBought: '#D8483B', // bear-red
  putSold: '#772821', // darker bear-red
  putNet: '#EEAFA9', // lighter bear-red
  netCallPremium: '#1C76A2', // bull-blue
  netPutPremium: '#D8483B', // bear-red
  premiumPressure: '#C2A14E', // accent-yellow
  spotPrice: '#9ca3af', // gray

  /**
   * ⚠️ Candles are MONOCHROME on purpose — `--color-candle-up` /
   * `--color-candle-down` in index.css, which exist for exactly this.
   *
   * They were bull-blue and bear-red: the same two colours that mean CALL and
   * PUT premium on this chart. Price and flow then shared a vocabulary, so a
   * run of blue candles read as call buying. Bone and grey leave blue and red
   * meaning one thing only.
   */
  candleUp: '#F4F2ED',
  candleDown: '#61615E',
};

// View modes
type ViewMode = 'calls' | 'puts' | 'premium';

// Format large numbers
function formatPremium(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '--';
  const absValue = Math.abs(value);
  if (absValue >= 1e9) return '$' + (value / 1e9).toFixed(2) + 'B';
  if (absValue >= 1e6) return '$' + (value / 1e6).toFixed(1) + 'M';
  if (absValue >= 1e3) return '$' + (value / 1e3).toFixed(1) + 'K';
  return '$' + value.toFixed(0);
}

function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '--';
  return '$' + value.toFixed(2);
}

// Check if after market close (16:00 ET)
function isAfterMarketClose(): boolean {
  const now = new Date();
  const etString = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
  const et = new Date(etString);
  return et.getHours() >= 16;
}

// Check if we should show historical data
function shouldShowHistorical(): boolean {
  return isWeekend() || isBeforeMarketOpen() || isAfterMarketClose();
}

export function CumulativePremiumV2Widget({ windowId, onRemove }: Props) {
  // Whether this pane is following the shared ticker. The box goes
  // read-only while it is — see the note on the input.
  const { isWindowLinked } = useTickerLink();
  const isTickerLinked = isWindowLinked(windowId);
  // State
  const [ticker, setTicker] = useState('SPXW');
  const [tickerInput, setTickerInput] = useState(getOptionsDisplayTicker('SPXW'));
  /**
   * Fixed to Premium — the switcher is gone (see the toolbar).
   *
   * ⚠️ Declared as state without a setter on purpose. A plain
   * `const viewMode: ViewMode = 'premium'` gets narrowed by TS to the literal
   * type, which makes the `viewMode === 'calls'` branches below provably false
   * and turns them into type errors. Those branches are unreachable now but
   * left intact in case the switcher ever returns.
   */
  const [viewMode] = useState<ViewMode>('premium');
  const [showNet, setShowNet] = useState(false); // Toggle for net in calls/puts view
  const [showPressure, setShowPressure] = useState(true); // Toggle for pressure in premium view
  const [showSpotPrice, setShowSpotPrice] = useState(true);
  const [nextExpirationOnly, setNextExpirationOnly] = useState(false); // Toggle for next expiration vs all
  const [latestData, setLatestData] = useState<PremiumSnapshot | null>(null);

  // Use candlesticks when OHLC data is available, otherwise fall back to spot line
  const showCandlesticks = true;

  // Historical mode state
  const [isHistorical, setIsHistorical] = useState(shouldShowHistorical());
  const [effectiveDate, setEffectiveDate] = useState(getEffectiveMarketDate());

  // Refs
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInitializedRef = useRef(false);
  const timeseriesDataRef = useRef<PremiumTimeseriesPoint[]>([]);
  // Last point the cold-snap guard accepted. The guard anchors on this rather
  // than on the array tail, so one bad point cannot become the baseline and
  // disarm the guard for the rest of the session.
  const lastTrustedRef = useRef<PremiumTimeseriesPoint | null>(null);
  const ohlcDataRef = useRef<OHLCPoint[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const isLoadedRef = useRef(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updateChartRef = useRef<() => void>(() => {});
  const chartSchedulerRef = useRef(createCoalescedScheduler(400));
  const tickerRef = useRef(ticker);
  tickerRef.current = ticker;
  const nextExpRef = useRef(nextExpirationOnly);
  nextExpRef.current = nextExpirationOnly;

  // Zoom state preservation - stores user's zoom/pan so it persists across data updates
  const zoomStateRef = useRef<{
    xaxis?: [string | number, string | number];
    yaxis?: [number, number];
    yaxis2?: [number, number];
  } | null>(null);

  // Update historical state periodically (every minute)
  useEffect(() => {
    const checkHistorical = () => {
      setIsHistorical(shouldShowHistorical());
      setEffectiveDate(getEffectiveMarketDate());
    };

    const interval = setInterval(checkHistorical, 60000);
    return () => clearInterval(interval);
  }, []);

  // Ref to track latest settings for flush-on-unmount
  const settingsRef = useRef({
    ticker, viewMode, showNet, showPressure, showSpotPrice, nextExpirationOnly,
  });
  settingsRef.current = {
    ticker, viewMode, showNet, showPressure, showSpotPrice, nextExpirationOnly,
  };

  // Load state from backend on mount
  useEffect(() => {
    const loadState = async () => {
      try {
        const response = await profileService.getFrontendState();
        const saved = response.frontend_state?.cumulativePremiumWidgets?.[windowId] as {
          ticker?: string;
          viewMode?: ViewMode;
          showNet?: boolean;
          showPressure?: boolean;
          showSpotPrice?: boolean;
          nextExpirationOnly?: boolean;
        } | undefined;
        if (saved) {
          if (saved.ticker) {
            setTicker(saved.ticker);
            setTickerInput(getOptionsDisplayTicker(saved.ticker));
          }
          // saved.viewMode is deliberately ignored: existing users may have
          // 'calls' or 'puts' persisted, and with the switcher gone they would
          // be stranded in a view they cannot leave.
          if (saved.showNet !== undefined) setShowNet(saved.showNet);
          if (saved.showPressure !== undefined) setShowPressure(saved.showPressure);
          if (saved.showSpotPrice !== undefined) setShowSpotPrice(saved.showSpotPrice);
          if (saved.nextExpirationOnly !== undefined) setNextExpirationOnly(saved.nextExpirationOnly);
        }
        isLoadedRef.current = true;
      } catch (err) {
        console.error('Failed to load CumulativePremium state:', err);
        isLoadedRef.current = true;
      }
    };
    loadState();
  }, [windowId]);

  // Save state (debounced 1s)
  useEffect(() => {
    if (!isLoadedRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      saveTimeoutRef.current = null;
      try {
        await profileService.saveFrontendState({
          cumulativePremiumWidgets: {
            [windowId]: { ticker, viewMode, showNet, showPressure, showSpotPrice, nextExpirationOnly },
          },
        });
      } catch (err) {
        console.error('Failed to save CumulativePremium state:', err);
      }
    }, 1000);
  }, [windowId, ticker, viewMode, showNet, showPressure, showSpotPrice, nextExpirationOnly]);

  // Flush pending save on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        profileService.saveFrontendState({
          cumulativePremiumWidgets: {
            [windowId]: settingsRef.current,
          },
        }).catch(() => {});
      }
    };
  }, [windowId]);

  // Plotly
  const { Plotly, isLoading: plotlyLoading, error: plotlyError } = usePlotly();

  // Fetch initial timeseries data
  const {
    data: timeseriesResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['premiumV2Timeseries', ticker, nextExpirationOnly, effectiveDate],
    queryFn: () => premiumV2Service.getTimeseries([ticker], nextExpirationOnly, effectiveDate),
    staleTime: 60 * 1000,
    // No polling interval. REST seeds history on mount, on tab refocus, and on
    // WS (re)connect (see the connect-refetch effect below); the QF Centrifugo
    // WS owns every live update after that. A periodic poll fought the WS for
    // the tail and snapped the chart back every ~15s (the flicker).
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });

  // Fetch OHLC data for candlestick chart
  const { data: ohlcResponse } = useQuery({
    queryKey: ['premiumV2OHLC', ticker, effectiveDate],
    queryFn: () => premiumV2Service.getOHLCTimeseries(ticker, effectiveDate),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false, // Don't retry if OHLC data isn't available
  });

  // Seed / refresh from REST without wiping live tip (merge keeps profile tail).
  useEffect(() => {
    if (timeseriesResponse && timeseriesResponse.length > 0) {
      const rest = timeseriesResponse[0].data;
      timeseriesDataRef.current = mergePremiumHistory(rest, timeseriesDataRef.current);
      // REST is authoritative: re-anchor the guard's baseline on its tail. Also
      // clears any baseline carried over from a previously-selected ticker.
      lastTrustedRef.current = rest[rest.length - 1] ?? null;
      const lastPoint = timeseriesDataRef.current[timeseriesDataRef.current.length - 1];
      if (lastPoint) {
        setLatestData({
          ticker,
          call_bought: lastPoint.cum_call_bought,
          call_sold: lastPoint.cum_call_sold,
          net_call_premium: lastPoint.cum_net_call_premium,
          put_bought: lastPoint.cum_put_bought,
          put_sold: lastPoint.cum_put_sold,
          net_put_premium: lastPoint.cum_net_put_premium,
          premium_pressure: lastPoint.cum_premium_pressure,
          spot_price: lastPoint.spot_price,
          next_expiration_only: nextExpirationOnly,
          timestamp: lastPoint.bucket,
        });
      }
    }
  }, [timeseriesResponse, ticker, nextExpirationOnly]);

  // Update OHLC data ref
  useEffect(() => {
    if (ohlcResponse?.data) {
      ohlcDataRef.current = ohlcResponse.data;
    } else {
      ohlcDataRef.current = [];
    }
  }, [ohlcResponse]);

  // Live cumulative-premium updates from QF profile:{ticker} snapshot.
  useEffect(() => {
    if (isHistorical) return;
    if (!ticker) return;

    // Spot-only update: QF publishes no single-leg premium book for this root,
    // or the incoming book was rejected. Keep the price overlay live without
    // touching the premium series.
    const applySpotOnly = (spot: number | null, asOf: string): void => {
      if (spot == null) return;
      const arr = timeseriesDataRef.current;
      const last = arr[arr.length - 1];
      if (last) last.spot_price = spot;
      setLatestData((prev) =>
        prev ? { ...prev, spot_price: spot, timestamp: asOf } : prev,
      );
    };

    const unsub = subscribePremiumLive(
      ticker,
      nextExpirationOnly,
      (point) => {
        const arr = timeseriesDataRef.current;
        const last = arr[arr.length - 1];
        // Compare against the last TRUSTED tail, not merely the last element.
        // Anchoring on arr[last] let a single bad point become the baseline and
        // permanently disarm the guard (its magnitude fails the >10k test, so
        // every later collapse was waved through).
        const baseline = lastTrustedRef.current ?? last;
        if (!shouldAcceptLivePremium(baseline, point)) {
          applySpotOnly(point.spot_price, point.bucket);
          return;
        }
        // profile snapshots arrive ~5x/sec; the point is a running cumulative total
        // for the current minute. Replace the last bucket if it's the same minute,
        // else append a new minute. Use premiumMinuteKey (UTC-normalized) so REST
        // bare minutes and live as_of Z timestamps compare equal.
        if (last && premiumMinuteKey(last.bucket) === premiumMinuteKey(point.bucket)) {
          arr[arr.length - 1] = point;
        } else {
          arr.push(point);
          if (arr.length > MAX_PREMIUM_POINTS) {
            arr.splice(0, arr.length - MAX_PREMIUM_POINTS);
          }
        }
        lastTrustedRef.current = point;
        // Flush reads refs only so a trailing tick after ticker change cannot
        // apply the previous root's totals.
        chartSchedulerRef.current.schedule(() => {
          const tail = lastTrustedRef.current;
          if (tail) {
            setLatestData({
              ticker: tickerRef.current,
              call_bought: tail.cum_call_bought,
              call_sold: tail.cum_call_sold,
              net_call_premium: tail.cum_net_call_premium,
              put_bought: tail.cum_put_bought,
              put_sold: tail.cum_put_sold,
              net_put_premium: tail.cum_net_put_premium,
              premium_pressure: tail.cum_premium_pressure,
              spot_price: tail.spot_price,
              next_expiration_only: nextExpRef.current,
              timestamp: tail.bucket,
            });
          }
          updateChartRef.current();
        });
      },
      applySpotOnly,
    );
    return () => {
      chartSchedulerRef.current.cancel();
      unsub();
    };
  }, [ticker, nextExpirationOnly, isHistorical]);

  // Re-seed history from REST whenever the QF WS (re)connects. Fires on the
  // initial connect and after any drop, so a reconnect backfills the gap; the
  // live subscription above then resumes ownership of the tail. No polling.
  useEffect(() => {
    if (isHistorical) return;
    return onQfConnected(() => {
      refetch();
    });
  }, [isHistorical, refetch]);

  // Live forming-bar OHLC from QF Centrifugo (forming bar ~1s + final at minute close).
  // Price data keys off the underlying INDEX ticker (SPX), never the options root
  // (SPXW) — ohlc:SPXW:1m has no data, which is why SPX showed no candles.
  useEffect(() => {
    if (isHistorical) return;
    const root = underlyingPriceTicker(ticker.toUpperCase());
    return qfSubscribe(`ohlc:${root}:1m`, (raw) => {
      const d = raw as { minute: string; open: number; high: number; low: number; close: number };
      // Normalize the live minute to the bare-UTC string the QF /v1/ohlc history
      // bucket uses, so the dedup findIndex matches the same minute and the candle
      // x-axis stays consistent with the (bare) history + premium traces.
      // bucketToDate guards a bare-or-Z input to UTC before re-flattening.
      const bucket = bucketToDate(d.minute).toISOString().slice(0, 19);
      const newPoint: OHLCPoint = { bucket, open: d.open, high: d.high, low: d.low, close: d.close, volume: null };
      const existingIdx = ohlcDataRef.current.findIndex((p) => p.bucket === newPoint.bucket);
      if (existingIdx >= 0) {
        ohlcDataRef.current[existingIdx] = newPoint;
      } else {
        ohlcDataRef.current.push(newPoint);
        if (ohlcDataRef.current.length > MAX_OHLC_POINTS) {
          ohlcDataRef.current.splice(0, ohlcDataRef.current.length - MAX_OHLC_POINTS);
        }
      }
      chartSchedulerRef.current.schedule(() => updateChartRef.current());
    });
  }, [ticker, isHistorical]);

  // Reset chart at market open - flush previous day's data
  useMarketOpen(() => {
    chartSchedulerRef.current.cancel();
    timeseriesDataRef.current = [];
    ohlcDataRef.current = [];
    chartInitializedRef.current = false;
    zoomStateRef.current = null;
    setLatestData(null);
    setIsHistorical(false);
    setEffectiveDate(getTodayDateET());
    // Delay refetch to give the data pipeline time to produce the first bucket.
    // After this seed, the WS connect-refetch effect + live subscription keep
    // the chart current (no periodic polling).
    setTimeout(() => refetch(), 60_000);
  });

  // Create/update Plotly chart
  const updateChart = useCallback(() => {
    if (!chartRef.current || !Plotly) return;

    const data = timeseriesDataRef.current;
    if (data.length === 0) return;

    const times = data.map((d) => bucketToDate(d.bucket));
    const traces: Plotly.Data[] = [];

    if (viewMode === 'calls') {
      // Calls view: call_bought, call_sold, optionally net_call_premium
      traces.push({
        x: times,
        y: data.map((d) => d.cum_call_bought),
        type: 'scatter',
        mode: 'lines',
        name: 'Call Bought',
        line: { color: COLORS.callBought, width: 2 },
        yaxis: 'y2',
        hovertemplate: 'Bought: $%{y:,.0f}<extra></extra>',
      });
      traces.push({
        x: times,
        y: data.map((d) => d.cum_call_sold),
        type: 'scatter',
        mode: 'lines',
        name: 'Call Sold',
        line: { color: COLORS.callSold, width: 2 },
        yaxis: 'y2',
        hovertemplate: 'Sold: $%{y:,.0f}<extra></extra>',
      });
      if (showNet) {
        traces.push({
          x: times,
          y: data.map((d) => d.cum_call_bought + d.cum_call_sold),
          type: 'scatter',
          mode: 'lines',
          name: 'Net Call',
          line: { color: COLORS.callNet, width: 2, dash: 'dash' },
          yaxis: 'y2',
          hovertemplate: 'Net: $%{y:,.0f}<extra></extra>',
        });
      }
    } else if (viewMode === 'puts') {
      // Puts view: put_bought, put_sold, optionally net_put_premium
      traces.push({
        x: times,
        y: data.map((d) => d.cum_put_bought),
        type: 'scatter',
        mode: 'lines',
        name: 'Put Bought',
        line: { color: COLORS.putBought, width: 2 },
        yaxis: 'y2',
        hovertemplate: 'Bought: $%{y:,.0f}<extra></extra>',
      });
      traces.push({
        x: times,
        y: data.map((d) => d.cum_put_sold),
        type: 'scatter',
        mode: 'lines',
        name: 'Put Sold',
        line: { color: COLORS.putSold, width: 2 },
        yaxis: 'y2',
        hovertemplate: 'Sold: $%{y:,.0f}<extra></extra>',
      });
      if (showNet) {
        traces.push({
          x: times,
          y: data.map((d) => d.cum_put_bought + d.cum_put_sold),
          type: 'scatter',
          mode: 'lines',
          name: 'Net Put',
          line: { color: COLORS.putNet, width: 2, dash: 'dash' },
          yaxis: 'y2',
          hovertemplate: 'Net: $%{y:,.0f}<extra></extra>',
        });
      }
    } else {
      // Premium view: net_call_premium, net_put_premium, optionally premium_pressure
      traces.push({
        x: times,
        y: data.map((d) => d.cum_call_bought + d.cum_call_sold),
        type: 'scatter',
        mode: 'lines',
        name: 'Net Call Premium',
        line: { color: COLORS.netCallPremium, width: 2 },
        yaxis: 'y2',
        hovertemplate: 'Call: $%{y:,.0f}<extra></extra>',
      });
      traces.push({
        x: times,
        y: data.map((d) => d.cum_put_bought + d.cum_put_sold),
        type: 'scatter',
        mode: 'lines',
        name: 'Net Put Premium',
        line: { color: COLORS.netPutPremium, width: 2 },
        yaxis: 'y2',
        hovertemplate: 'Put: $%{y:,.0f}<extra></extra>',
      });
      if (showPressure) {
        traces.push({
          x: times,
          y: data.map((d) => d.cum_premium_pressure),
          type: 'scatter',
          mode: 'lines',
          name: 'Premium Pressure',
          line: { color: COLORS.premiumPressure, width: 2 },
          yaxis: 'y2',
          hovertemplate: 'Pressure: $%{y:,.0f}<extra></extra>',
        });
      }
    }

    // Price trace - either candlesticks (from OHLC) or line (from premium data)
    if (showSpotPrice) {
      const ohlcData = ohlcDataRef.current;
      if (showCandlesticks && ohlcData.length > 0) {
        // Use OHLC candlestick trace
        const ohlcTimes = ohlcData.map((d) => bucketToDate(d.bucket));
        traces.push({
          x: ohlcTimes,
          open: ohlcData.map((d) => d.open),
          high: ohlcData.map((d) => d.high),
          low: ohlcData.map((d) => d.low),
          close: ohlcData.map((d) => d.close),
          type: 'candlestick',
          name: 'Price',
          yaxis: 'y',
          increasing: { line: { color: COLORS.candleUp }, fillcolor: COLORS.candleUp },
          decreasing: { line: { color: COLORS.candleDown }, fillcolor: COLORS.candleDown },
          hoverinfo: 'x+y',
        } as Plotly.Data);
      } else {
        // Fallback to line chart from spot price in premium data
        traces.push({
          x: times,
          y: data.map((d) => d.spot_price),
          type: 'scatter',
          mode: 'lines',
          name: 'Spot Price',
          line: { color: COLORS.spotPrice, width: 1.5 },
          yaxis: 'y',
          hovertemplate: 'Spot: $%{y:.2f}<extra></extra>',
        });
      }
    }

    // Logo watermark. The Fuzion mark, matching the Gamma Exposure widget,
    // rather than the WSJ one this carried — that asset predates the rebrand
    // and is still used by GEX-Ray, which is a separate call.
    const images: Partial<Plotly.Image>[] = [
      {
        source: '/logo-watermark.png',
        xref: 'paper',
        yref: 'paper',
        x: 0.5,
        y: 0.5,
        sizex: 0.25,
        sizey: 0.25,
        xanchor: 'center',
        yanchor: 'middle',
        // 0.10, not 0.15. The Fuzion mark is bone on dark and reads heavier
        // than the old one at the same value, and a watermark that competes
        // with the data is worse than none.
        opacity: 0.10,
        layer: 'below',
      },
    ];

    // Build layout with preserved zoom state
    const savedZoom = zoomStateRef.current;
    const layout: Partial<Plotly.Layout> = {
      images,
      paper_bgcolor: COLORS.background,
      plot_bgcolor: COLORS.background, // Darker plot area (matching GEXRay V3)
      font: { color: COLORS.text },
      margin: { l: 60, r: 60, t: 10, b: 30 },
      showlegend: false,
      hovermode: 'x unified',
      dragmode: 'pan',
      xaxis: {
        type: 'date',
        gridcolor: COLORS.gridLines,
        tickformat: '%H:%M',
        rangeslider: { visible: false },
        // Apply saved zoom state for x-axis
        ...(savedZoom?.xaxis && { range: savedZoom.xaxis }),
      },
      yaxis: {
        side: 'right',
        showgrid: false,
        tickformat: '.2f',
        title: undefined,
        // Apply saved zoom state for y-axis (price)
        ...(savedZoom?.yaxis && { range: savedZoom.yaxis }),
      },
      yaxis2: {
        side: 'left',
        overlaying: 'y',
        gridcolor: COLORS.gridLines,
        tickformat: '.2s',
        title: undefined,
        // Apply saved zoom state for y2-axis (premium)
        ...(savedZoom?.yaxis2 && { range: savedZoom.yaxis2 }),
      },
    };

    const config: Partial<Plotly.Config> = {
      responsive: true,
      displayModeBar: false,
      scrollZoom: false,
    };

    if (!chartInitializedRef.current) {
      Plotly.newPlot(chartRef.current, traces, layout, config);
      chartInitializedRef.current = true;

      // Add relayout listener to capture zoom/pan state
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (chartRef.current as any).on('plotly_relayout', (eventdata: Record<string, unknown>) => {
        // User zoomed or panned - save the new ranges
        if (eventdata['xaxis.range[0]'] !== undefined || eventdata['yaxis.range[0]'] !== undefined) {
          zoomStateRef.current = {
            ...(eventdata['xaxis.range[0]'] !== undefined && {
              xaxis: [eventdata['xaxis.range[0]'], eventdata['xaxis.range[1]']] as [string | number, string | number],
            }),
            ...(eventdata['yaxis.range[0]'] !== undefined && {
              yaxis: [eventdata['yaxis.range[0]'], eventdata['yaxis.range[1]']] as [number, number],
            }),
            ...(eventdata['yaxis2.range[0]'] !== undefined && {
              yaxis2: [eventdata['yaxis2.range[0]'], eventdata['yaxis2.range[1]']] as [number, number],
            }),
            // Preserve any existing zoom state for axes not being changed
            ...(!eventdata['xaxis.range[0]'] && zoomStateRef.current?.xaxis && { xaxis: zoomStateRef.current.xaxis }),
            ...(!eventdata['yaxis.range[0]'] && zoomStateRef.current?.yaxis && { yaxis: zoomStateRef.current.yaxis }),
            ...(!eventdata['yaxis2.range[0]'] && zoomStateRef.current?.yaxis2 && { yaxis2: zoomStateRef.current.yaxis2 }),
          };
        }
        // User double-clicked to reset zoom (autorange)
        if (eventdata['xaxis.autorange'] === true || eventdata['yaxis.autorange'] === true) {
          zoomStateRef.current = null;
        }
      });
    } else {
      Plotly.react(chartRef.current, traces, layout, config);
    }
  }, [Plotly, viewMode, showNet, showPressure, showSpotPrice, showCandlesticks]);

  updateChartRef.current = updateChart;

  // REST seed / Plotly ready / settings: draw now. Live ticks stay coalesced.
  useEffect(() => {
    if (Plotly && timeseriesDataRef.current.length > 0) {
      updateChartRef.current();
    }
  }, [
    Plotly,
    timeseriesResponse,
    ohlcResponse,
    viewMode,
    showNet,
    showPressure,
    showSpotPrice,
    showCandlesticks,
  ]);

  // Handle resize
  useEffect(() => {
    if (!chartRef.current || !Plotly) return;

    const resizeObserver = new ResizeObserver(() => {
      if (chartRef.current && chartInitializedRef.current) {
        Plotly.Plots.resize(chartRef.current);
      }
    });

    resizeObserver.observe(chartRef.current);
    return () => resizeObserver.disconnect();
  }, [Plotly]);

  // Cleanup on unmount
  useEffect(() => {
    const chartEl = chartRef.current;
    const scheduler = chartSchedulerRef.current;
    return () => {
      scheduler.cancel();
      if (chartEl && Plotly) {
        Plotly.purge(chartEl);
      }
      chartInitializedRef.current = false;
    };
  }, [Plotly]);

  // Handle ticker change
  const handleTickerSubmit = () => {
    const newTicker = normalizeOptionsTicker(tickerInput.trim().toUpperCase());
    if (newTicker && newTicker !== ticker) {
      chartSchedulerRef.current.cancel();
      setTicker(newTicker);
      timeseriesDataRef.current = [];
      ohlcDataRef.current = [];
      chartInitializedRef.current = false;
      zoomStateRef.current = null;
    }
  };

  const handleSelectTicker = (selected: string) => {
    const normalized = normalizeOptionsTicker(selected);
    chartSchedulerRef.current.cancel();
    setTickerInput(getOptionsDisplayTicker(normalized));
    setTicker(normalized);
    timeseriesDataRef.current = [];
    ohlcDataRef.current = [];
    chartInitializedRef.current = false;
    zoomStateRef.current = null;
    inputRef.current?.blur();
  };

  // Get color class for value
  const getValueColorClass = (value: number | null) => {
    if (value === null) return 'text-gray-400';
    return value >= 0 ? 'text-bull-blue' : 'text-bear-red';
  };

  // View toggle button component
  // On/off toggle. Selected uses the shared Accent Gold treatment.
  const ViewButton = ({
    label,
    isActive,
    onClick,
  }: {
    label: string;
    isActive: boolean;
    onClick: () => void;
  }) => (
    <button
      onClick={onClick}
      className={`px-2 py-0.5 text-xs rounded transition-colors border ${
        isActive
          ? 'bg-accent-yellow border-accent-yellow text-hot-tag-text font-semibold'
          : 'border-dark-border text-gray-400 hover:text-white hover:border-gray-500'
      }`}
    >
      {label}
    </button>
  );

  // Loading state
  if (plotlyLoading) {
    return (
      <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
        <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Net Sweeper Flow</h3>
        </div>
        <div className="flex-1 flex items-center justify-center text-gray-400">
          Loading chart library...
        </div>
      </div>
    );
  }

  if (plotlyError) {
    return (
      <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
        <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Net Sweeper Flow</h3>
        </div>
        <div className="flex-1 flex items-center justify-center text-bear-red">
          Error loading chart library
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      {/* Title Bar - drag handle */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">
            Net Sweeper Flow — <span className="text-accent-yellow">{getOptionsDisplayTicker(ticker)}</span>
            {ohlcResponse?.underlying && ohlcResponse.underlying !== getOptionsDisplayTicker(ticker) && (
              <span className="text-gray-400 font-normal ml-1">({ohlcResponse.underlying})</span>
            )}
          </h3>
          {isHistorical && (
            <span className="px-1.5 py-0.5 text-xs font-semibold uppercase tracking-[0.5px] bg-accent-yellow text-hot-tag-text rounded">
              {formatDateForDisplay(effectiveDate)}
            </span>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-dark-bg/50 border-b border-dark-border relative z-20 gap-2 flex-wrap">
        {/* Left side: Ticker input. Type + Enter; no autocomplete. */}
        <div className="flex items-center gap-2 relative">
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={tickerInput}
              onChange={(e) => {
                setTickerInput(e.target.value.toUpperCase());
              }}
              onFocus={() => {
                // See the note above — no clearing while the box is read-only.
                if (isTickerLinked) return;
                setTickerInput('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleTickerSubmit();
                } else if (e.key === 'Escape') {
                  inputRef.current?.blur();
                }
              }}
              onBlur={() => {
                setTimeout(() => handleTickerSubmit(), 150);
              }}
              readOnly={isTickerLinked}
              title={isTickerLinked ? 'Linked — set the ticker from another linked widget or unlink to edit' : undefined}
/**
                 ⚠️ Read-only while linked. The symbol is owned by the link
                 group, so editing it here would either be silently discarded
                 or would move every other linked pane — neither of which a
                 plain-looking text field leads anyone to expect. The gold
                 border is the tell. */
              className={`w-24 px-2 py-0.5 text-xs border rounded text-accent-yellow font-semibold tracking-wide placeholder:font-normal placeholder:tracking-normal placeholder-gray-500 focus:outline-none ${
                isTickerLinked
                  ? 'bg-dark-surface border-accent-yellow ring-1 ring-accent-yellow/40 cursor-default select-none'
                  : 'bg-dark-surface border-dark-border focus:border-accent-yellow'
              }`}
              placeholder="Ticker"
            />
          </div>
          <TickerLinkButton
            windowId={windowId}
            currentTicker={ticker}
            onTickerChange={handleSelectTicker}
          />
          <button
            onClick={() => refetch()}
            className="px-2 py-0.5 text-xs bg-dark-surface-light hover:bg-dark-surface border border-dark-border rounded text-gray-300 transition-colors"
            title="Refresh data"
          >
            ↻
          </button>
        </div>

        {/* Center: View toggle buttons */}
        <div className="flex items-center gap-1">
          {/* Calls / Puts / Premium switcher removed — the widget is Net
              Sweeper FLOW, and Premium is the view that name describes. The
              other two were a different chart wearing the same title.
              The Net toggle went with them: it only ever applied to the
              calls/puts views, so on Premium it was a control that did
              nothing. Pressure, which is the Premium-only toggle, stays and
              no longer needs its condition. */}
          <ViewButton
            label="Pressure"
            isActive={showPressure}
            onClick={() => setShowPressure(!showPressure)}
          />
          {/* Spot/OHLC toggle */}
          <ViewButton
            label={showCandlesticks && ohlcDataRef.current.length > 0 ? 'OHLC' : 'Spot'}
            isActive={showSpotPrice}
            onClick={() => setShowSpotPrice(!showSpotPrice)}
          />
          {/* Separates the on/off toggles from the Exp selector */}
          <div className="w-px h-4 bg-dark-border flex-shrink-0 mx-1" />

          {/* Expiration filter. A segmented pair rather than a toggle: it picks
              between two states, and the single button it replaced changed its
              own label — reading "All Exp" when off and "Next Exp" when on. So
              the text told you either the current state or what clicking would
              do, with no way to tell which. Both options stay visible and the
              selected one is filled, which cannot be misread.

              Its `color="#6366f1"` went too — stock indigo, off-palette, and
              inline, which is how it survived the palette sweep. */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-[1px] text-gray-500">Exp</span>
            <div className="flex items-center rounded border border-dark-border overflow-hidden">
              {([
                { label: 'Next', value: true },
                { label: 'All', value: false },
              ] as const).map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => setNextExpirationOnly(opt.value)}
                  aria-pressed={nextExpirationOnly === opt.value}
                  className={`px-2 py-0.5 text-xs transition-colors ${
                    nextExpirationOnly === opt.value
                      ? 'bg-accent-yellow text-hot-tag-text font-semibold'
                      : 'bg-transparent text-gray-400 hover:text-white hover:bg-dark-surface'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right side: Close button */}
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-0.5 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Chart Container */}
      <div className="relative flex-1 overflow-hidden">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-dark-surface/80 z-10">
            <span className="text-gray-400 text-sm">Loading data...</span>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-dark-surface/80 z-10">
            <span className="text-bear-red text-sm">
              {error instanceof Error ? error.message : 'Failed to load data'}
            </span>
          </div>
        )}

        <div ref={chartRef} className="w-full h-full" />
      </div>

      {/* Footer Stats */}
      <div className="px-3 py-1.5 border-t border-dark-border flex items-center gap-4 bg-dark-bg/50 text-xs overflow-x-auto">
        <div className="flex items-center gap-1 whitespace-nowrap">
          <span className="text-gray-500">Call:</span>
          <span className={getValueColorClass(latestData != null ? latestData.call_bought + latestData.call_sold : null)}>
            {formatPremium(latestData != null ? latestData.call_bought + latestData.call_sold : undefined)}
          </span>
        </div>
        <div className="flex items-center gap-1 whitespace-nowrap">
          <span className="text-gray-500">Put:</span>
          <span className={getValueColorClass(latestData != null ? latestData.put_bought + latestData.put_sold : null)}>
            {formatPremium(latestData != null ? latestData.put_bought + latestData.put_sold : undefined)}
          </span>
        </div>
        <div className="flex items-center gap-1 whitespace-nowrap">
          <span className="text-gray-500">Pressure:</span>
          <span className={getValueColorClass(latestData?.premium_pressure ?? null)}>
            {formatPremium(latestData?.premium_pressure)}
          </span>
        </div>
        <div className="flex items-center gap-1 whitespace-nowrap">
          <span className="text-gray-500">Spot:</span>
          <span className="text-gray-300">{formatPrice(latestData?.spot_price)}</span>
        </div>
      </div>
    </div>
  );
}
